import { useState, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { categories, articles } from '@/mocks/articles';

export default function CategoriesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCat = searchParams.get('cat') || 'all';

  const filteredArticles = useMemo(() => {
    if (activeCat === 'all') return articles;
    return articles.filter((a) => a.category === activeCat);
  }, [activeCat]);

  const handleCategoryChange = (catId: string) => {
    if (catId === 'all') {
      setSearchParams({});
    } else {
      setSearchParams({ cat: catId });
    }
  };

  return (
    <>
      <ScrollToTop />
      <Header />
      <main className="min-h-screen bg-background-50">
        <section className="relative w-full h-[260px] md:h-[340px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Abstract%20geometric%20landscape%20with%20warm%20golden%20and%20cream%20gradients%2C%20soft%20flowing%20shapes%2C%20minimalist%20elegant%20composition%2C%20sophisticated%20atmosphere%2C%20modern%20design%20elements%20with%20organic%20curves&width=1600&height=600&seq=jmtt-categories-hero&orientation=landscape"
            alt="分类浏览"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/30 to-black/55"></div>
          <div className="relative z-10 text-center px-4">
            <h1 className="text-2xl md:text-4xl font-heading font-bold text-white mb-3">
              分类浏览
            </h1>
            <p className="text-white/60 text-sm md:text-base max-w-lg mx-auto">
              探索JMTT最新资讯、教程与行业洞见
            </p>
          </div>
        </section>

        <nav className="max-w-[1400px] mx-auto px-4 md:px-6 -mt-8 relative z-10" aria-label="面包屑导航">
          <ol className="flex items-center gap-2 text-xs text-foreground-500 bg-background-50 rounded-lg px-4 py-3 border border-background-200/70" itemScope itemType="https://schema.org/BreadcrumbList">
            <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
              <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer" itemProp="item">
                <span itemProp="name">首页</span>
              </Link>
              <meta itemProp="position" content="1" />
            </li>
            <li><span className="text-foreground-300">/</span></li>
            <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
              <span className="text-foreground-700 font-medium" itemProp="name">分类浏览</span>
              <meta itemProp="position" content="2" />
            </li>
          </ol>
        </nav>

        <section className="max-w-[1400px] mx-auto px-4 md:px-6 py-8 md:py-12">
          <div className="flex flex-wrap items-center gap-2 mb-8" role="tablist" aria-label="文章分类">
            {categories.map((cat) => (
              <button
                key={cat.id}
                role="tab"
                aria-selected={activeCat === cat.id}
                onClick={() => handleCategoryChange(cat.id)}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                  activeCat === cat.id
                    ? 'bg-primary-500 text-white'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                <i className={`${cat.icon} text-sm`}></i>
                {cat.name}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {filteredArticles.map((article) => (
              <article key={article.id} className="group rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-200/70 transition-all duration-300 overflow-hidden cursor-pointer">
                <Link to={`/article/${article.id}`} className="block">
                  <div className="aspect-[16/9] overflow-hidden">
                    <img
                      src={article.image}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-400"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="px-2.5 py-0.5 rounded-md bg-accent-100 text-accent-700 text-xs font-medium">
                        {categories.find((c) => c.id === article.category)?.name}
                      </span>
                      <span className="text-foreground-400 text-xs">{article.date}</span>
                    </div>
                    <h3 className="font-heading font-semibold text-foreground-900 text-sm md:text-base mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                      {article.title}
                    </h3>
                    <p className="text-foreground-500 text-xs leading-relaxed line-clamp-2 mb-4">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5 text-foreground-400 text-xs">
                        <i className="ri-time-line"></i>
                        <span>{article.readTime}</span>
                      </div>
                      <span className="text-primary-600 text-xs font-medium flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        阅读全文
                        <i className="ri-arrow-right-line"></i>
                      </span>
                    </div>
                  </div>
                </Link>
              </article>
            ))}
          </div>

          {filteredArticles.length === 0 && (
            <div className="text-center py-16">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200">
                <i className="ri-inbox-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500 text-sm">该分类下暂无文章</p>
            </div>
          )}
        </section>
      </main>
      <Footer />
    </>
  );
}